// GroupedMeasurements — renders the FULL scan payload (~90 keys, decision
// 03/07: keep and SHOW all measurements) grouped by body region:
// Composição / Circunferências / Medidas frontais / Medidas de perfil.
// Used by the scan result screen AND the history detail sheet (same pattern).
// Labels resolve via i18n (fitness.bodyScan.m.<group>.<key>); an unmapped key
// (future vendor additions) falls back to humanized snake_case so new payload
// fields never render blank.

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";

const CIRC_KEYS = [
  "neck", "chest", "waist", "abdomen", "low_hips", "high_hips", "bicep",
  "forearm", "wrist", "thigh", "mid_thigh_girth", "knee", "calf", "ankle",
  "neck_girth", "neck_girth_relaxed", "elbow_girth", "armscye_girth",
  "overarm_girth", "under_bust_girth", "upper_knee_girth", "upper_bicep_girth",
  "upper_chest_girth", "alternative_waist_girth", "thigh_1_inch_below_crotch",
] as const;

const COMP_KEYS = [
  "fat_percentage", "bmi", "bmr", "fat_body_mass", "lean_body_mass", "estimated_weight",
] as const;

const COMP_UNIT: Record<string, string> = {
  fat_percentage: "%",
  bmi: "",
  bmr: " kcal",
  fat_body_mass: " kg",
  lean_body_mass: " kg",
  estimated_weight: " kg",
};

function humanize(key: string): string {
  return key.replace(/_/g, " ");
}

function fmt(v: unknown): string | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return String(Math.round(v * 10) / 10);
}

export function GroupedMeasurements({ raw }: { raw: Record<string, unknown> }) {
  const { t } = useLanguage();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({ composition: true });

  const composition = (raw.composition || {}) as Record<string, unknown>;
  const circ = (raw.circumference_params || {}) as Record<string, unknown>;
  const front = (raw.front_linear_params || {}) as Record<string, unknown>;
  const side = (raw.side_linear_params || {}) as Record<string, unknown>;
  const bodyType = (raw.body_type ?? circ.body_type) as string | null;

  const label = (group: string, key: string): string => {
    const k = `fitness.bodyScan.m.${group}.${key}`;
    const v = t(k);
    return v === k ? humanize(key) : v; // fallback: unmapped vendor key
  };

  const groups: Array<{ id: string; titleKey: string; rows: Array<{ label: string; value: string; unit: string }> }> = [];

  const compRows = COMP_KEYS
    .map((k) => {
      const v = fmt(composition[k]);
      return v === null ? null : { label: label("comp", k), value: v, unit: COMP_UNIT[k] ?? "" };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
  if (bodyType) compRows.push({ label: label("comp", "body_type"), value: bodyType, unit: "" });
  if (compRows.length) groups.push({ id: "composition", titleKey: "fitness.bodyScan.group.composition", rows: compRows });

  const circRows = CIRC_KEYS
    .map((k) => {
      const v = fmt(circ[k]);
      return v === null ? null : { label: label("circ", k), value: v, unit: " cm" };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
  if (circRows.length) groups.push({ id: "circumference", titleKey: "fitness.bodyScan.group.circumference", rows: circRows });

  // Front/side linear params: render EVERY numeric key present (56 + 8 in the
  // FitXpress contract; tolerant to additions). body_area_percentage is %.
  const linearRows = (group: string, obj: Record<string, unknown>) =>
    Object.keys(obj)
      .sort()
      .map((k) => {
        const v = fmt(obj[k]);
        if (v === null) return null;
        return { label: label(group, k), value: v, unit: k === "body_area_percentage" ? "%" : " cm" };
      })
      .filter((r): r is { label: string; value: string; unit: string } => r !== null);

  const frontRows = linearRows("front", front);
  if (frontRows.length) groups.push({ id: "front", titleKey: "fitness.bodyScan.group.frontLinear", rows: frontRows });
  const sideRows = linearRows("side", side);
  if (sideRows.length) groups.push({ id: "side", titleKey: "fitness.bodyScan.group.sideLinear", rows: sideRows });

  if (groups.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">{t("fitness.bodyScan.scan.noDetails")}</p>;
  }

  return (
    <div className="space-y-2">
      {groups.map((g) => (
        <div key={g.id} className="rounded-xl overflow-hidden" style={{ border: "1px solid hsl(222 18% 18%)" }}>
          <button
            onClick={() => setOpenGroups((s) => ({ ...s, [g.id]: !s[g.id] }))}
            className="w-full flex items-center justify-between px-3 py-2.5"
            style={{ background: "hsl(222 18% 12%)" }}
          >
            <span className="text-[11px] font-display font-bold tracking-wide uppercase" style={{ color: "hsl(187 72% 60%)" }}>
              {t(g.titleKey)} <span className="text-muted-foreground font-normal">({g.rows.length})</span>
            </span>
            <ChevronDown size={14} className={`transition-transform ${openGroups[g.id] ? "rotate-180" : ""}`} style={{ color: "hsl(187 72% 60%)" }} />
          </button>
          {openGroups[g.id] && (
            <div className="px-3 py-1" style={{ background: "hsl(222 18% 9%)" }}>
              {g.rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: i < g.rows.length - 1 ? "1px solid hsl(222 18% 14%)" : "none" }}>
                  <span className="text-[12px] text-foreground/60 capitalize">{r.label}</span>
                  <span className="text-[12px] font-semibold text-foreground">{r.value}{r.unit}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
